import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "../../components/Logo";

const NAV = [
  { to: "/features",      label: "Features" },
  { to: "/how-it-works",  label: "How it works" },
  { to: "/videos",        label: "Videos" },
  { to: "/pricing",       label: "Pricing" },
  { to: "/security",      label: "Security" },
  { to: "/faq",           label: "FAQ" },
  { to: "/contact",       label: "Contact" },
];

export default function MarketingLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  return (
    <div className="min-h-screen bg-[#0B1220] text-slate-100 font-display antialiased selection:bg-emerald-400/30">
      {/* Ambient cinematic glow that follows the page (CSS only — no 3D libs needed) */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[700px] h-[700px] rounded-full bg-emerald-500/10 blur-[140px] animate-pulse-slow" />
        <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-emerald-400/[0.07] blur-[160px]" style={{ animation: "drift 18s ease-in-out infinite alternate" }} />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full bg-emerald-300/[0.05] blur-[150px]" style={{ animation: "drift 22s ease-in-out infinite alternate-reverse" }} />
      </div>

      {/* Top nav */}
      <header className={`sticky top-0 z-40 backdrop-blur-xl ${onHome ? "bg-[#0B1220]/40" : "bg-[#0B1220]/80"} border-b border-white/[0.06]`}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 h-16 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="marketing-logo">
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-400 rounded-lg blur-md opacity-40" />
              <Logo size={36} color="#10B981" />
            </div>
            <div className="hidden sm:block">
              <div className="font-bold text-lg leading-none">Onco<span className="text-emerald-400">Cadence</span></div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 mt-0.5">Chemotherapy Treatment Companion</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm transition-colors ${isActive ? "text-emerald-400" : "text-slate-300 hover:text-white"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden sm:inline-flex text-sm text-slate-300 hover:text-white" data-testid="nav-sign-in">Sign in</Link>
            <Link
              to="/demo"
              data-testid="nav-request-demo"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0B1220] font-bold text-sm transition-colors shadow-lg shadow-emerald-500/20"
            >
              Request Demo
            </Link>
            <button onClick={() => setOpen((v) => !v)} className="lg:hidden p-2 text-slate-300 hover:text-white" aria-label="Menu">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-white/[0.06] bg-[#0B1220]/95">
            <div className="px-6 py-3 space-y-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="block px-3 py-2 text-sm text-slate-300 hover:text-emerald-400">{n.label}</NavLink>
              ))}
              <Link to="/login" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm text-slate-300 hover:text-emerald-400">Sign in</Link>
            </div>
          </div>
        )}
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="border-t border-white/[0.06] mt-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Logo size={28} color="#10B981" />
              <span className="font-bold">OncoCadence</span>
            </div>
            <p className="text-sm text-slate-500 leading-relaxed">A bidirectional chemotherapy treatment companion — uniting providers and patients through every cycle, every dose, every visit.</p>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">Product</div>
            <div className="space-y-2 text-sm text-slate-400">
              <Link to="/features" className="block hover:text-emerald-400">Features</Link>
              <Link to="/how-it-works" className="block hover:text-emerald-400">How it works</Link>
              <Link to="/videos" className="block hover:text-emerald-400">Videos</Link>
              <Link to="/pricing" className="block hover:text-emerald-400">Pricing</Link>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">Company</div>
            <div className="space-y-2 text-sm text-slate-400">
              <Link to="/security" className="block hover:text-emerald-400">Security</Link>
              <Link to="/faq" className="block hover:text-emerald-400">FAQ</Link>
              <Link to="/contact" className="block hover:text-emerald-400">Contact</Link>
              <Link to="/demo" className="block hover:text-emerald-400">Request demo</Link>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">Talk to us</div>
            <div className="space-y-2 text-sm text-slate-400">
              <a href="mailto:DEMO@BioMedMeet.com" className="block hover:text-emerald-400">DEMO@BioMedMeet.com</a>
              <a href="tel:+14848026153" className="block hover:text-emerald-400">+1 (484) 802-6153</a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/[0.06] py-6 text-center text-xs text-slate-600">© {new Date().getFullYear()} OncoCadence — Secure provider &amp; patient portal · HIPAA-ready</div>
      </footer>
    </div>
  );
}
